/*
 * Color math for runtime branding: an agency's accent becomes the panel's
 * brand ramp (50 to 900), worked out in OKLCH so the tints keep the accent's
 * hue, then nudged until text on and around it meets WCAG AA.
 */

export type Rgb = [number, number, number];

export function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1]!;
  if (h.length === 3) h = h.replace(/./g, (c) => c + c);
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export function toHex([r, g, b]: Rgb): string {
  const c = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

const toLinear = (v: number) => {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const fromLinear = (c: number) => 255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

/** WCAG relative luminance. */
export function luminance(rgb: Rgb): number {
  const [r, g, b] = rgb.map(toLinear) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 to 21. */
export function contrast(a: Rgb | string, b: Rgb | string): number {
  const x = typeof a === "string" ? parseHex(a) : a;
  const y = typeof b === "string" ? parseHex(b) : b;
  if (!x || !y) return 1;
  const [hi, lo] = [luminance(x), luminance(y)].sort((p, q) => q - p) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

type Lch = { l: number; c: number; h: number };

function rgbToOklch(rgb: Rgb): Lch {
  const [r, g, b] = rgb.map(toLinear) as Rgb;
  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  return { l: L, c: Math.hypot(A, B), h: (Math.atan2(B, A) * 180) / Math.PI };
}

function oklchToLinear({ l, c, h }: Lch): [number, number, number] {
  const A = c * Math.cos((h * Math.PI) / 180);
  const B = c * Math.sin((h * Math.PI) / 180);
  const l_ = (l + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m_ = (l - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s_ = (l - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  ];
}

/** OKLCH to sRGB, lowering chroma until the color fits (so a vivid hue never clips to a different one). */
function oklchToRgb(lch: Lch): Rgb {
  let c = lch.c;
  for (let i = 0; i < 40; i++) {
    const lin = oklchToLinear({ ...lch, c });
    if (lin.every((v) => v >= -0.0005 && v <= 1.0005)) return lin.map((v) => fromLinear(Math.min(1, Math.max(0, v)))) as Rgb;
    c *= 0.9;
  }
  return oklchToLinear({ ...lch, c: 0 }).map((v) => fromLinear(Math.min(1, Math.max(0, v)))) as Rgb;
}

export const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
export type Shade = (typeof SHADES)[number];
export type Ramp = Record<Shade, string>;

/** The lightness of each shade, from Hosterr's own orange ramp, so any accent gets the same rhythm. */
const LIGHTNESS: Record<Shade, number> = { 50: 0.975, 100: 0.94, 200: 0.87, 300: 0.8, 400: 0.72, 500: 0.6, 600: 0.54, 700: 0.46, 800: 0.39, 900: 0.31 };
/** Pale shades carry less color, as tints do. */
const CHROMA: Record<Shade, number> = { 50: 0.12, 100: 0.25, 200: 0.5, 300: 0.75, 400: 0.92, 500: 1, 600: 1, 700: 0.95, 800: 0.85, 900: 0.7 };

const WHITE: Rgb = [255, 255, 255];
const CANVAS: Rgb = [239, 241, 244]; // --hx-canvas, light
const NIGHT: Rgb = [12, 14, 18]; // --hx-canvas, dark
const NIGHT_SURFACE: Rgb = [21, 24, 30]; // --hx-surface, dark

export type BrandCheck = {
  ramp: Ramp;
  /** Did any shade have to move away from the even ramp to stay readable? */
  adjusted: boolean;
  /** Is the accent itself the 500 shade? A very light or very dark accent gets a shade near it instead. */
  accentKept: boolean;
  /** White text on the button color (brand-600). */
  button: number;
  /** Link color (brand-600) on the light page. */
  link: number;
  /** Link color on the dark page (brand-300). */
  linkDark: number;
};

/**
 * The ramp for an accent: 500 is the accent's hue at Hosterr's lightness,
 * and 600 (buttons, links, checked controls) and 300 (links in the dark
 * theme) move until they reach AA (4.5:1) against what they sit on.
 */
export function brandRamp(accent: string): BrandCheck | null {
  const rgb = parseHex(accent);
  if (!rgb) return null;
  const base = rgbToOklch(rgb);
  // Grays have no meaningful hue; keep them gray rather than inventing one.
  const c = base.c < 0.02 ? 0 : Math.max(base.c, 0.06);
  const make = (shade: Shade, l: number) => oklchToRgb({ l, c: c * CHROMA[shade], h: base.h });

  const ls = { ...LIGHTNESS };
  // The accent itself sits at 500 when it's in the usual range, so the brand color shows as chosen.
  const accentKept = base.l > 0.45 && base.l < 0.72;
  if (accentKept) {
    ls[500] = base.l;
    ls[400] = Math.min(ls[300] - 0.04, base.l + 0.1);
    ls[600] = Math.min(ls[600], base.l - 0.05);
  }
  let adjusted = !accentKept;
  // White on 600, and 600 on the light page: at least 4.5:1, with a little margin.
  while (contrast(WHITE, make(600, ls[600])) < 4.6 || contrast(CANVAS, make(600, ls[600])) < 4.6) {
    ls[600] -= 0.01;
    adjusted = true;
    if (ls[600] < 0.2) break;
  }
  ls[700] = Math.min(ls[700], ls[600] - 0.07);
  ls[800] = Math.min(ls[800], ls[700] - 0.07);
  ls[900] = Math.min(ls[900], ls[800] - 0.07);
  // 500 is used for focus rings and borders: 3:1 against the page is enough for those.
  while (contrast(CANVAS, make(500, ls[500])) < 3 && ls[500] > 0.3) {
    ls[500] -= 0.01;
    adjusted = true;
  }
  // The dark theme reads links and active items in 300 and 200: 4.5:1 on its surfaces.
  while ((contrast(NIGHT, make(300, ls[300])) < 4.6 || contrast(NIGHT_SURFACE, make(300, ls[300])) < 4.6) && ls[300] < 0.95) {
    ls[300] += 0.01;
    adjusted = true;
  }
  ls[200] = Math.max(ls[200], ls[300] + 0.06);
  ls[100] = Math.max(ls[100], ls[200] + 0.06);
  ls[50] = Math.max(ls[50], Math.min(0.985, ls[100] + 0.03));

  const ramp = Object.fromEntries(SHADES.map((s) => [s, toHex(make(s, Math.min(0.99, ls[s])))])) as Ramp;
  return {
    ramp,
    adjusted,
    accentKept: accentKept && toHex(make(500, ls[500])).toLowerCase() === toHex(rgb).toLowerCase(),
    button: contrast(WHITE, ramp[600]),
    link: contrast(CANVAS, ramp[600]),
    linkDark: contrast(NIGHT, ramp[300]),
  };
}

const INK: Rgb = [12, 14, 18]; // near-black, the dark canvas

/**
 * The brand as a solid fill for "where you are" (the active environment, a selected segment), and the text on it.
 * The agency's own color as it is, with white or near-black text, whichever reads better, when that reaches
 * 4.5:1: a light accent (yellow) gets near-black text, a dark one (navy) white. When neither does (a mid-tone, like
 * Hosterr's own orange at 4.43:1 with white), the color is darkened only as far as white text needs, keeping its
 * hue and chroma, so it still reads as the brand and not as a brown or a second red.
 */
export function solidBrand(accent: string, ramp: Ramp): { fill: string; text: string; ratio: number } {
  const rgb = parseHex(accent);
  if (!rgb) return { fill: ramp[600], text: "#ffffff", ratio: contrast(WHITE, ramp[600]) };
  const onWhite = contrast(WHITE, rgb);
  const onInk = contrast(INK, rgb);
  if (Math.max(onWhite, onInk) >= 4.5) return onWhite >= onInk ? { fill: toHex(rgb), text: "#ffffff", ratio: onWhite } : { fill: toHex(rgb), text: toHex(INK), ratio: onInk };
  const lch = rgbToOklch(rgb);
  for (let l = lch.l; l > 0.2; l -= 0.0025) {
    const c = oklchToRgb({ ...lch, l });
    const r = contrast(WHITE, c);
    if (r >= 4.5) return { fill: toHex(c), text: "#ffffff", ratio: r };
  }
  return { fill: ramp[600], text: "#ffffff", ratio: contrast(WHITE, ramp[600]) };
}

/** "4.5:1" */
export const ratioWords = (n: number) => `${(Math.floor(n * 10) / 10).toFixed(1)}:1`;

/* ── Status colors ─────────────────────────────────────────────── */

/**
 * The four status tones (docs/ux-principles.md, "Color means something"), fixed whatever the brand. The same values
 * as styles.css (--hx-good, --hx-warn, --hx-bad, --hx-info and their -fill), for code that needs them as colors:
 * the palette on the components page and the brand check below. `text` reads on white and the canvas at 4.5:1 or
 * better; `fill` is the solid for dots, bars and chart lines (3:1 on white).
 */
export const STATUS = {
  good: { name: "Good", light: { text: "#20663a", fill: "#3ba05f", bg: "#edfbf0" }, dark: { text: "#78d694", fill: "#51af6f" } },
  warn: { name: "Warning", light: { text: "#985406", fill: "#d47d05", bg: "#fffaeb" }, dark: { text: "#f8c35a", fill: "#f5ab2c" } },
  bad: { name: "Critical", light: { text: "#be123c", fill: "#e11d48", bg: "#fff1f3" }, dark: { text: "#fda4af", fill: "#f43f5e" } },
  info: { name: "Info", light: { text: "#2457c5", fill: "#3b74ec", bg: "#eff5ff" }, dark: { text: "#8fb5fb", fill: "#5f92f6" } },
} as const;
export type StatusKey = keyof typeof STATUS;

/**
 * Which status colors an accent sits close to (similar hue, both clearly colored): a red brand near Critical, a
 * green one near Good, a blue one near Info. Nothing breaks: status never uses the brand, and every status also
 * has its icon and words. The Branding page says so, so nobody wonders whether a red button means a problem.
 */
export function nearStatus(accent: string): StatusKey[] {
  const rgb = parseHex(accent);
  if (!rgb) return [];
  const a = rgbToOklch(rgb);
  if (a.c < 0.06) return [];
  return (Object.keys(STATUS) as StatusKey[]).filter((k) => {
    const s = rgbToOklch(parseHex(STATUS[k].light.fill)!);
    const d = Math.abs(((a.h - s.h + 540) % 360) - 180);
    return d < 22;
  });
}

/* ── Tag colors ────────────────────────────────────────────────── */

/** The sixteen preset tag colors (the API's keys), each as its mid shade. */
export const TAG_PRESET_HEX = {
  slate: "#64748b",
  red: "#ef4444",
  orange: "#f97316",
  amber: "#f59e0b",
  yellow: "#eab308",
  lime: "#84cc16",
  green: "#22c55e",
  teal: "#14b8a6",
  cyan: "#06b6d4",
  sky: "#0ea5e9",
  blue: "#3b82f6",
  indigo: "#6366f1",
  violet: "#8b5cf6",
  purple: "#a855f7",
  pink: "#ec4899",
  rose: "#f43f5e",
} as const;
export type TagPreset = keyof typeof TAG_PRESET_HEX;
export const TAG_PRESETS = Object.keys(TAG_PRESET_HEX) as TagPreset[];

/** How a tag chip looks in one theme: a soft tint, its hairline, a solid dot, and text that reads on the tint. */
export type TagTone = { bg: string; fg: string; dot: string; ring: string };

const tagTones = new Map<string, { light: TagTone; dark: TagTone }>();

/**
 * A tag's chip in both themes, from a preset key or a custom #rrggbb, worked out in OKLCH so every color gets the
 * same treatment: a pale tint of its hue, a dot in the color itself (kept visible on the tint and the page), and
 * text in a deep (light theme) or pale (dark theme) shade of the hue, moved until it reads at 4.5:1 on the tint.
 * Grays stay gray. Unknown values fall back to slate.
 */
export function tagTone(color: string | null | undefined): { light: TagTone; dark: TagTone } {
  const key = (color ?? "slate").toLowerCase();
  const hit = tagTones.get(key);
  if (hit) return hit;
  const hex = key in TAG_PRESET_HEX ? TAG_PRESET_HEX[key as TagPreset] : (parseHex(key) ? key : TAG_PRESET_HEX.slate);
  const base = rgbToOklch(parseHex(hex)!);
  const gray = base.c < 0.03;
  const c = gray ? Math.min(base.c, 0.02) : base.c;
  const at = (l: number, chroma: number) => oklchToRgb({ l, c: chroma, h: base.h });

  // Light: a tint near white, deep text, the dot at the color's own lightness (kept between 0.5 and 0.72).
  const lBg = at(0.962, Math.min(c * 0.32, 0.045));
  const lRing = at(0.885, Math.min(c * 0.55, 0.09));
  let lText = 0.5;
  while (contrast(at(lText, Math.min(c, 0.16)), lBg) < 4.6 && lText > 0.2) lText -= 0.01;
  // The dot: 3:1 on white at least, so the color is there to see.
  let lDotL = Math.min(0.7, Math.max(0.5, base.l));
  while (contrast(at(lDotL, c), [255, 255, 255]) < 3 && lDotL > 0.3) lDotL -= 0.01;
  const lDot = at(lDotL, c);
  // Dark: a deep tint on the dark page, pale text, the dot a step lighter so it shows.
  const dBg = at(0.3, Math.min(c * 0.4, 0.07));
  const dRing = at(0.4, Math.min(c * 0.6, 0.1));
  let dText = 0.84;
  while (contrast(at(dText, Math.min(c * 0.7, 0.12)), dBg) < 4.6 && dText < 0.98) dText += 0.01;
  const dDot = at(Math.min(0.8, Math.max(0.64, base.l + 0.06)), c);

  const out = {
    light: { bg: toHex(lBg), fg: toHex(at(lText, Math.min(c, 0.16))), dot: toHex(lDot), ring: toHex(lRing) },
    dark: { bg: toHex(dBg), fg: toHex(at(dText, Math.min(c * 0.7, 0.12))), dot: toHex(dDot), ring: toHex(dRing) },
  };
  tagTones.set(key, out);
  return out;
}

/** A tag chip's colors as CSS variables, for the classes in TagChip (light and dark). */
export function tagVars(color: string | null | undefined): Record<string, string> {
  const t = tagTone(color);
  return {
    "--tag-bg": t.light.bg,
    "--tag-fg": t.light.fg,
    "--tag-dot": t.light.dot,
    "--tag-ring": t.light.ring,
    "--tag-bg-d": t.dark.bg,
    "--tag-fg-d": t.dark.fg,
    "--tag-dot-d": t.dark.dot,
    "--tag-ring-d": t.dark.ring,
  };
}

/** A color's name for people: "Teal", or the hex for a custom one. */
export const tagColorName = (color: string | null | undefined) => {
  const c = (color ?? "slate").toLowerCase();
  return c in TAG_PRESET_HEX ? c[0]!.toUpperCase() + c.slice(1) : c;
};
